export const tasksCopy = {
  projects: {
    title: "Projects",
    /** The h1 is written with its full stop, as the frames draw every screen title. */
    heading: "Projects",
    active: "Active",
    archived: "Archived",
    newProject: "New project",
    columns: {
      name: "Name",
      lead: "Lead",
      people: "People",
      due: "Due",
      progress: "Progress",
    },
    private: "Private",
    noDueDate: "No due date",
    dueToday: "Due today",
    overdue: (days: number) =>
      days === 1 ? "1 day overdue" : `${days} days overdue`,
    of: (done: number, total: number) => `${done} of ${total}`,
    none: "No projects yet",
    /** Said by a screen reader where a cell shows only a dash. */
    missing: { lead: "No lead", progress: "Progress not shared" },
  },
  project: {
    back: "Projects",
    heading: (name: string) => name,
    done: (done: number, total: number) => `${done} of ${total} done`,
    groups: { in_progress: "In progress", todo: "To do", done: "Done" },
    addTask: "Add a task",
    addTo: (group: string) => `Add a task to ${group}`,
    addAnother: "Add another",
    enterKey: "↵",
    esc: "Esc",
    close: "Close",
    due: (day: string) => `Due ${day}`,
    wasDue: (day: string) => `Was due ${day}`,
    people: (count: number) => (count === 1 ? "1 person" : `${count} people`),
    lead: "Lead",
    you: (name: string) => `${name} (you)`,
    /** Heard on each chip, whose visible text is only the value. */
    chips: {
      label: "Project details",
      lead: "Lead",
      due: "Due",
      access: "Who can see it",
      people: "People",
    },
    access: {
      company: "Everyone",
      private: "Private",
      companyLong: "Everyone in the company",
      companyHint: (company: string) => `Anyone at ${company} can see it.`,
      privateHint: "Only people in this project, and admins.",
    },
    menu: {
      label: "Project actions",
      rename: "Rename",
      copyLink: "Copy link",
      archive: "Archive project",
      set: { lead: "Set lead", people: "Add people", due: "Set due date" },
    },
    linkNotCopied: "The link was not copied. Copy it from the address bar.",
    archived: (day: string) =>
      `Archived ${day}. Its tasks are hidden from Home and Tasks.`,
    delete: "Delete",
    unarchive: "Unarchive",
    deleted: (name: string) => `Deleted “${name}”.`,
    gone: {
      title: "This project isn't available",
      line: "It was deleted, or it's private.",
      back: "Back to Projects",
    },
    members: {
      search: "Add people",
      open: (count: number) => `${count} open`,
      remove: "Remove",
      notIn: "Not in this project",
      add: "Add",
    },
  },
  newProject: {
    title: "New project",
    name: "Name",
    lead: "Lead",
    due: "Due",
    client: "Client",
    none: "None",
    access: "Who can see it",
    company: "Everyone in the company",
    private: "Private",
    people: "People",
    addPeople: "Add people",
    cancel: "Cancel",
    create: "Create project",
    createKey: "↵",
    close: "Close",
  },
  refused: {
    taskNotFound: "We could not find that task.",
    notATitle: "Give it a name of 500 characters or fewer.",
    notANote: "Keep notes to 10,000 characters or fewer.",
    notAPriority: "Priority is a number from 0 to 3.",
    notARepeat: "That is not a repeat we recognise.",
    /** A drop the server cannot make: the task or the one above it has left my list or its day. */
    listChanged:
      "Your list changed meanwhile, so that task stayed where it was.",
    notAProjectName: "Give the project a name of 200 characters or fewer.",
    notAClientName: "Give the client a name of 200 characters or fewer.",
    notACountry: "Pick a country.",
    notACurrency: "Pick a currency.",
    notAnEmail: "Enter an email address.",
    notTerms: "Payment terms are 0 to 365 days.",
    tooLong: (max: number) => `Keep it to ${max} characters.`,
    notAComment: "Write a comment of 10,000 characters or fewer.",
    tooManyImages: "Add up to 10 images to one comment.",
    notADoneList: "Name a project or a parent task.",
    notASearch: "Search for 1 to 200 characters.",
  },
  image: {
    tooLarge: "Pick an image under 10 MB.",
    notAnImage: "Pick a JPEG, a PNG, a WebP or a GIF.",
    lengthRequired: "That image arrived without its size. Try again.",
  },
  quickAdd: {
    title: "Add task",
    placeholder: "Call Marcus tomorrow at 4 pm",
    notes: "Notes",
    date: "Date",
    priority: "Priority",
    projects: "Projects",
    assignee: "Assignee",
    /** The assignee chip's name, which carries the name it shows. */
    assigneeIs: (name: string) => `Assignee: ${name}`,
    cancel: "Cancel",
    add: "Add task",
    enter: "↵",
    foreday: "Foreday AI",
    you: (name: string) => `${name} (you)`,
    /** Open tasks in a project, beside its name in the # list. */
    openTasks: (count: number) => (count === 1 ? "1 task" : `${count} tasks`),
    clear: (what: string) => `Remove ${what}`,
    added: (project: string | null, due: string | null) =>
      [project ? `Added to ${project}` : "Added", due && `due ${due}`]
        .filter(Boolean)
        .join(", "),
    open: "Open",
  },
  deleted: {
    title: "Recently deleted",
    show: {
      all: "All",
      tasks: "Tasks",
      projects: "Projects",
      invoices: "Invoices",
    },
    kept: "Kept for 30 days, then removed for good.",
    empty: "Nothing deleted in the last 30 days.",
    emptyShown: {
      all: "Nothing deleted in the last 30 days.",
      tasks: "No tasks deleted in the last 30 days.",
      projects: "No projects deleted in the last 30 days.",
      invoices: "No invoice drafts deleted in the last 30 days.",
    },
    noProject: "No project",
    noClient: "No client",
    invoiceDraft: "Invoice draft",
    project: (tasks: number) =>
      `Project, ${tasks} ${tasks === 1 ? "task" : "tasks"}`,
    ago: (days: number) =>
      days === 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`,
    /** No name when the deletion has no audit row to say who. */
    deletedBy: (name: string | null, ago: string) =>
      name ? `Deleted by ${name} · ${ago}` : `Deleted ${ago}`,
    left: (days: number) => `${days} ${days === 1 ? "day" : "days"} left`,
    restore: "Restore",
    restored: (name: string) => `Restored “${name}”.`,
    restoredDraft: (client: string | null) =>
      client
        ? `Restored the invoice draft for ${client}.`
        : "Restored the invoice draft.",
    open: "Open",
  },
  /** The task panel: frames 19 to 22 and 03 of `docs/design/tasks/task-panel-and-repeat.md`. */
  panel: {
    close: "Close",
    start: "Start",
    gone: {
      title: "This task isn't available",
      line: "It was deleted, or it's private.",
    },
    more: "More",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    duplicate: "Duplicate",
    duplicated: "Task duplicated",
    open: "Open",
    deleteTask: "Delete task",
    deleted: "Task deleted",
    moveTo: "Move to project",
    moveUp: "Move up",
    moveDown: "Move down",
    noProject: "No project",
    /** Who reads a task with no project: its creator and whoever holds it. */
    onlySees: (names: readonly string[]) =>
      `Only ${names.join(" and ")} will see it.`,
    you: "you",
    title: "Title",
    status: "Status",
    assignee: "Assignee",
    due: "Due",
    priority: "Priority",
    notes: "Notes",
    addNotes: "Add notes",
    notesFailed: "The notes didn't load.",
    subtasks: "Subtasks",
    addSubtask: "Add a subtask",
    comments: "Comments",
    commentsFailed: "The comments didn't load.",
    /** "1 of 3": subtasks done of all of them. */
    doneOf: (done: number, all: number) => `${done} of ${all}`,
    today: "Today",
    tomorrow: "Tomorrow",
    noDate: "No date",
    /** "Today at 11:00 am". */
    dueAt: (day: string, time: string) => `${day} at ${time}`,
    /** The activity line under Comments: "2 updates, last by Foreday at 9:02 am". */
    updates: (count: number, who: string, at: string) =>
      `${count} ${count === 1 ? "update" : "updates"}, last by ${who} at ${at}`,
    /** The same line when the last update was before today: "last by Foreday on Thu 8 Oct". */
    updatesOn: (count: number, who: string, day: string) =>
      `${count} ${count === 1 ? "update" : "updates"}, last by ${who} on ${day}`,
    /** The same line on a repeating task: "Done 4 times, last on Mon 28 Sep". */
    doneTimes: (count: number, day: string) =>
      `Done ${count} ${count === 1 ? "time" : "times"}, last on ${day}`,
  },
  /** One line per change once the activity line is opened, after who made it. */
  activity: {
    created: "created this task",
    checkedOff: "checked it off",
    reopened: "reopened it",
    started: "started it",
    renamed: "renamed it",
    notes: "edited the notes",
    assigned: (name: string) => `assigned it to ${name}`,
    unassigned: "removed the assignee",
    handed: "handed it to Foreday AI",
    dated: (day: string) => `set it due ${day}`,
    undated: "removed the due date",
    timed: (clock: string) => `set the time to ${clock}`,
    untimed: "removed the time",
    priority: (level: string) => `set the priority to ${level}`,
    repeats: (rule: string) =>
      `set it to repeat ${rule.charAt(0).toLowerCase()}${rule.slice(1)}`,
    stopped: "stopped it repeating",
    repeatsFromDone: "set it to repeat from when it is checked off",
    repeatsFromDue: "set it to repeat from its due date",
    moved: "moved it",
    deleted: "deleted it",
    restored: "restored it",
    /** An outside assistant's change: "Claude for Sarah". */
    assistant: (assistant: string, person: string) =>
      `${assistant} for ${person}`,
    undo: "Undo",
    anAssistant: "An assistant",
    /** Two changes in one line: "renamed it and set it due Thu 8 Oct". */
    and: (first: string, rest: string) => `${first} and ${rest}`,
  },
  status: { todo: "To do", in_progress: "In progress", done: "Done" },
  /** Indexed by the stored priority, 0 to 3. */
  priority: ["No priority", "Low", "Medium", "High"],
  assignee: {
    search: "Assign to",
    foreday: "Foreday AI",
    you: (name: string) => `${name} (you)`,
    nobody: "No assignee",
  },
  time: {
    open: "Time",
    /** The chip's name, which a combobox does not take from its text. */
    label: (clock: string | null) => (clock ? `Time: ${clock}` : "Time"),
    search: "Type a time, like 3:30 pm",
    none: "No time",
  },
  repeat: {
    open: "Repeat",
    everyDay: "Every day",
    everyWeekday: "Every weekday",
    everyWeekOn: (weekday: string) => `Every week on ${weekday}`,
    everyMonthOn: (ordinal: string) => `Every month on the ${ordinal}`,
    everyYearOn: (day: string) => `Every year on ${day}`,
    /** The short form on a row and beside a due date: "Every Monday". */
    everyNamed: (weekday: string) => `Every ${weekday}`,
    everyWeek: "Every week",
    everyMonth: "Every month",
    everyYear: "Every year",
    /** "Every 2 weeks". */
    everyN: (count: number, unit: string) => `Every ${count} ${unit}`,
    /** "Every 2 weeks on Mon and Thu". */
    on: (rule: string, when: string) => `${rule} on ${when}`,
    onThe: (rule: string, ordinal: string) => `${rule} on the ${ordinal}`,
    and: "and",
    custom: "Custom…",
    customTitle: "Custom",
    every: "Every",
    fewer: "Fewer",
    more: "More",
    /** Singular, then plural; the frame draws "weeks". */
    units: {
      day: ["day", "days"],
      week: ["week", "weeks"],
      month: ["month", "months"],
      year: ["year", "years"],
    },
    onDays: "On",
    from: "Repeat from",
    fromDue: "Due date",
    fromCompleted: "The day it's checked off",
    stop: "Stop repeating",
    next: (day: string) => `Next: ${day}`,
    cancel: "Cancel",
    done: "Done",
    /** The toast after checking off a repeating task. */
    checkedOff: (day: string) => `Done · next ${day}`,
  },
  comments: {
    foreday: "Foreday AI",
    edited: "edited",
    placeholder: "Leave a comment…",
    send: "Send",
    addImages: "Add images",
    removeImage: "Remove image",
    openImage: (at: number, of: number) => `Image ${at} of ${of}`,
    dropTitle: "Drop images here",
    dropHint: "Images only.",
    notAnImage: (name: string) => `${name} isn't an image.`,
    mentionLabel: "Mention a person",
    /** Read for a mention of the person reading, whose chip alone would say it by colour. */
    mentionOfYou: (name: string) => `Mention of you, ${name}`,
    notPosted: "That comment wasn't posted.",
  },
  /** A link to one of these is named for the service; any other by its address. */
  services: {
    docs: "Google Docs",
    sheets: "Google Sheets",
    slides: "Google Slides",
    drive: "Google Drive",
    figma: "Figma",
  },
  viewer: {
    when: (moment: string, task: string) => `${moment} · ${task}`,
    today: (time: string) => `Today at ${time}`,
    onDay: (day: string, time: string) => `${day} at ${time}`,
    position: (at: number, of: number) => `${at} of ${of}`,
    download: "Download",
    close: "Close",
    previous: "Previous image",
    next: "Next image",
  },
  tasks: {
    /** The headline, and the page title. */
    title: "Tasks",
    pageTitle: "Tasks",
    overdue: "Overdue",
    today: "Today",
    upcoming: "Upcoming",
    noDate: "No date",
    moveToToday: "Move to today",
    addTask: "Add a task",
    done: "Done",
    /** The two views, mine and the ones I gave someone else. */
    views: "Whose tasks",
    mine: "Mine",
    given: "Assigned by me",
    givenNone: "Nothing you've assigned is open.",
  },
  row: {
    today: "Today",
    tomorrow: "Tomorrow",
    late: (day: string, days: number) =>
      `${day} · ${days} ${days === 1 ? "day" : "days"} late`,
    done: (title: string) => `Done: ${title}`,
    updated: (when: string) => `Updated ${when}`,
    subtasks: (done: number, total: number) => `${done}/${total}`,
    highPriority: "High priority",
    /** A handed-over task's line, by the state of the reader's latest run on it. */
    foreday: {
      running: "Foreday is working",
      waiting: "Foreday needs your answer",
      done: "Foreday is done, review it",
      failed: "Foreday couldn't finish",
      /** Handed over by someone else, whose run this reader cannot see. */
      handed: "Handed to Foreday",
    },
    /** Heard on the due chip, which opens the date picker. */
    changeDate: (when: string) => `${when}, change the date`,
    repeat: {
      day: "Every day",
      weekday: "Every weekday",
      week: "Every week",
      month: "Every month",
      year: "Every year",
      every: (count: number, unit: "day" | "week" | "month" | "year") =>
        `Every ${count} ${unit}s`,
    },
  },
  filter: {
    rangeTo: " – ",
    open: "Filter",
    add: "Add filter",
    describe: "Describe the filter",
    fields: {
      status__in: "Status",
      assignee_id__in: "Assignee",
      due_on__range: "Due date",
      priority__in: "Priority",
    },
    /** Pressed in the open menu, each opens its field; the values list shows it. */
    fieldKeys: {
      status__in: "S",
      assignee_id__in: "A",
      due_on__range: "D",
      priority__in: "P",
    },
    addKey: "F",
    you: (name: string) => `${name} (you)`,
    nobody: "No assignee",
    foreday: "Foreday AI",
    is: "is",
    isAnyOf: "is any of",
    remove: (field: string) => `Remove ${field}`,
    clear: "Clear",
    due: { overdue: "Overdue", today: "Today", week: "This week" },
    nothing: "Nothing matches these filters.",
    hidden: (count: number) => (count === 1 ? "1 task" : `${count} tasks`),
    hiddenBy: "hidden by filters",
    edit: "Edit your filters",
  },
  display: {
    open: "Display",
    grouping: "Grouping",
    ordering: "Ordering",
    groupings: {
      status: "Status",
      assignee: "Assignee",
      priority: "Priority",
      none: "No grouping",
    },
    orderings: { manual: "Manual", due: "Due date", priority: "Priority" },
    orderingHints: { due: "Soonest", priority: "Highest" },
  },
  /** The sheet's "On a task" rows. */
  keys: {
    move: "Go up or down",
    open: "Open",
    check: "Check off",
    status: "Status",
    assign: "Assign",
    due: "Due date",
    priority: "Priority",
    remove: "Delete",
    up: "Move task up",
    down: "Move task down",
  },
  moved: (title: string, day: string) => `Moved “${title}” to ${day}`,
  placed: (title: string) => `Moved “${title}”`,
  movedMany: (count: number, day: string) => `Moved ${count} tasks to ${day}`,
  undated: (title: string) => `Removed the date from “${title}”`,
  /** Foreday's last comment on a task a person took back while it was working or waiting. */
  takenBack: "This task was taken back, so Foreday AI stopped.",
  home: {
    tasksToday: (count: number, more: boolean) =>
      `${count} ${count === 1 ? "task" : "tasks"} today${more ? "," : ""}`,
    overdue: (count: number) => `${count} overdue`,
    nothingDue: "Nothing due today",
    moveOverdue: (count: number) => `Move ${count} to today`,
    addTask: "Add a task",
    doneToday: "Done today",
  },
} as const;
